import { Button } from "@/components/flexkit/Button";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Label } from "@/components/podkit/forms/Label";
import { DialogClose } from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useGetWebhookSecret, useUpdateWebhook, type PlainWebhook } from "@/queries/webhook-queries";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { useState, type FC } from "react";
import { WebhookScopeField } from "@/components/webhooks/WebhookScopeField";

type WebhookDetailsConfigurationStepProps = {
    webhook: PlainWebhook;
    onClose: () => void;
};

export const WebhookDetailsConfigurationStep: FC<WebhookDetailsConfigurationStepProps> = ({ webhook, onClose }) => {
    const { toast } = useToast();
    const { data: secretToken, isLoading: isLoadingSecret } = useGetWebhookSecret(webhook.id);
    const { mutateAsync: updateWebhook, isPending: isUpdating } = useUpdateWebhook();

    const provider = webhook.spec?.provider ?? WebhookProvider.UNSPECIFIED;
    const webhookType = webhook.spec?.type ?? WebhookType.UNSPECIFIED;
    const payloadUrl = webhook.url || "";

    const [selectedScope, setSelectedScope] = useState<string>(webhook.spec?.scope || "");

    return (
        <div className="-m-1 flex flex-col gap-4 p-1">
            <WebhookScopeField
                provider={provider}
                webhookType={webhookType}
                value={selectedScope}
                onChange={setSelectedScope}
                disabled={isUpdating}
                isNewWebhook
            />

            <div className="flex flex-col gap-2">
                <Label htmlFor="payload-url" className="text-sm font-medium">
                    Payload URL
                </Label>
                <CopyableInput
                    id="payload-url"
                    value={payloadUrl}
                    data-testid="payload-url-input"
                    data-tracking-id="copy-payload-url-webhook-setup"
                />
            </div>

            <div className="flex flex-col gap-2">
                <Label htmlFor="secret-token" className="text-sm font-medium">
                    Secret Token
                </Label>
                <CopyableInput
                    id="secret-token"
                    type="password"
                    value={secretToken || ""}
                    disabled={isLoadingSecret}
                    data-testid="secret-token-input"
                    data-tracking-id="copy-secret-token-webhook-setup"
                />
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <DialogClose asChild>
                    <Button variant="outline" type="button" data-testid="webhook-cancel" data-tracking-id="cancel">
                        Do later
                    </Button>
                </DialogClose>
                <Button
                    variant="primary"
                    type="button"
                    data-testid="webhook-done"
                    data-tracking-id="webhook-done"
                    disabled={!selectedScope || isUpdating}
                    loading={isUpdating}
                    onClick={async () => {
                        if (!selectedScope) return;
                        try {
                            await updateWebhook({ webhookId: webhook.id, scope: selectedScope });
                            toast({
                                title: "Webhook configured",
                                description: "You can now create an automation to use it.",
                            });
                            onClose();
                        } catch {
                            toast({
                                title: "Failed to save scope",
                                description: "Please try again.",
                            });
                        }
                    }}
                >
                    Done
                </Button>
            </div>
        </div>
    );
};
